import React from 'react'
import {
  Overflow,
  OverflowItem,
  useOverflowMenu,
  useIsOverflowItemVisible,
  Tab,
  TabList,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import { MoreHorizontalRegular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding(0, tokens.spacingHorizontalS),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    // Overflow only works if the row is allowed to be narrower than its content.
    minWidth: 0,
  },
  list: {
    flexGrow: 1,
    minWidth: 0,
    flexWrap: 'nowrap',
    overflowX: 'hidden',
  },
  menuButton: {
    flexShrink: 0,
    alignSelf: 'center',
  },
})

export interface PaneTab {
  id: string
  label: string
  icon?: React.ReactElement
  /** Optional count shown next to the label, as Office panes do for result sets. */
  count?: number
}

export interface PaneTabsProps {
  tabs: PaneTab[]
  selected: string
  onSelect: (id: string) => void
}

/**
 * Tabs that collapse into a "..." menu when the pane is too narrow to show them all.
 *
 * A task pane can be resized down to a very narrow column, and Office guidance is that no
 * command may become unreachable at that width. Widening the pane restores the tabs inline.
 */
export const PaneTabs: React.FC<PaneTabsProps> = ({ tabs, selected, onSelect }) => {
  const styles = useStyles()

  return (
    <div className={styles.root}>
      <Overflow minimumVisible={1}>
        <TabList
          className={styles.list}
          selectedValue={selected}
          onTabSelect={(_, data) => onSelect(data.value as string)}
          size="small"
        >
          {tabs.map(tab => (
            <OverflowItem key={tab.id} id={tab.id} priority={tab.id === selected ? 1 : 0}>
              <Tab value={tab.id} icon={tab.icon}>
                {tab.label}
                {typeof tab.count === 'number' && (
                  <>
                    {' '}
                    <Badge appearance="ghost" size="small">
                      {tab.count}
                    </Badge>
                  </>
                )}
              </Tab>
            </OverflowItem>
          ))}
          <OverflowTabsMenu tabs={tabs} onSelect={onSelect} className={styles.menuButton} />
        </TabList>
      </Overflow>
    </div>
  )
}

interface OverflowTabsMenuProps {
  tabs: PaneTab[]
  onSelect: (id: string) => void
  className?: string
}

/** The "..." trigger; renders only while at least one tab is hidden. */
const OverflowTabsMenu: React.FC<OverflowTabsMenuProps> = ({ tabs, onSelect, className }) => {
  const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>()

  if (!isOverflowing) return null

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          ref={ref}
          className={className}
          appearance="subtle"
          size="small"
          icon={<MoreHorizontalRegular />}
          aria-label={`${overflowCount} more tab${overflowCount === 1 ? '' : 's'}`}
          title={`${overflowCount} more`}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {tabs.map(tab => (
            <OverflowMenuItem key={tab.id} tab={tab} onSelect={onSelect} />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  )
}

/** A menu entry for one tab; hidden while that tab is visible in the bar. */
const OverflowMenuItem: React.FC<{ tab: PaneTab; onSelect: (id: string) => void }> = ({
  tab,
  onSelect,
}) => {
  const visible = useIsOverflowItemVisible(tab.id)

  if (visible) return null

  return (
    <MenuItem icon={tab.icon} onClick={() => onSelect(tab.id)}>
      {tab.label}
      {typeof tab.count === 'number' ? ` (${tab.count})` : ''}
    </MenuItem>
  )
}
